import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button, ListGroup, Alert, Form, Accordion, Spinner, Dropdown } from "react-bootstrap";
import styles from "./Inbox.module.css";
import { FaPen, FaEye, FaPaperclip, FaStar, FaRegClock, FaRegCommentDots } from "react-icons/fa";
import { FiArrowUpRight, FiArrowDownRight, FiClock, FiSearch } from "react-icons/fi";
import GenericFHIRViewerModal from "./cards/GenericFHIRViewerModal";
import CommunicationFormModal from "./modals/CommunicationFormModal";
import QuickThanksModal from "./modals/QuickThanksModal";
import CommQueryBuilderModal from "./modals/CommQueryBuilderModal";
import ThreadedMessageViewer from "./ThreadedMessageViewer"; // Adjust path if needed
import InfoBar from "./Infobar";
import { useDispatch, useSelector } from "react-redux";
import { addMessage, incrementPageNumber, decrementPageNumber, setPageNumber, clearNewMessages } from "./slices/messageSlice"; // Adjust path if needed
import { promotePending } from "./store/thunks/promotePending"; // path may vary
import { fetchProviderByReference } from "./services/providerService";
import { fetchPatientByReference } from "./services/patientService";
import { fetchEncounterByReference } from "./services/encounterService";
import { useStore } from "react-redux"; // for getState()
import CommViewerModal from "./CommViewerModal";
import { renderPriorityBadge } from "./utils/renderPriorityBadge";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import tooltipRegistry from "./tooltips/tooltipRegistry";
import { groupByDateRange } from "./utils/groupByDateRange";
import { getUrgentCount } from "./utils/getUrgentCount";
import { getStarredCount } from "./utils/getStarredCount";
import { filterLocalMessages } from "./utils/filterMessages";
import { hasBeenReadByCurrentUser, isSelfMessage } from "./utils/messageUtils";
import { formatDate } from "./utils/dateUtils";
import { fetchNewMessages } from "./store/thunks/fetchNewMessages"; // adjust path if needed
import { resolveName } from "./utils/resolveName"; // Adjust path as needed

function Inbox({ contextProvider, contextPatient, contextEncounter, error, fhirClient, communicationService }) {
    const dispatch = useDispatch();
    const store = useStore();
    const [selectedComm, setSelectedComm] = useState(null);
    const [fetchedPatient, setFetchedPatient] = useState(null);
    const [fetchedProvider, setFetchedProvider] = useState(null);
    const [fetchedEncounter, setFetchedEncounter] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const currentPage = useSelector(state => state.messages.currentPageNumber);
    const currentPageUrl = useSelector(state => state.messages.currentPageUrl);
    const [resolvedNames, setResolvedNames] = useState({});
    const hasNewMessage = useSelector(state => state.messages.hasNewMessage);
    const messagesToShow = useSelector(state => {
        if (!currentPageUrl) return [];

        const page = state.messages.pageMap[currentPageUrl];
        if (!page || !page.ids) return [];

        const existingMessages = page.ids.map(id => state.messages.byId[id]);
        const existingIds = new Set(page.ids);

        const newMessages = currentPage === 1 ? Object.values(state.messages.newMessages.byId || {}).filter(msg => msg?.id && !existingIds.has(msg.id)) : [];
        const merged = [...existingMessages, ...newMessages]
            .filter(msg => msg?.sent)
            .sort((a, b) => new Date(b.sent) - new Date(a.sent));

        return merged;
    });


    const nextPageUrl = useSelector(state => {
        if (!state.messages.currentPageUrl) return null;
        const page = state.messages.pageMap[state.messages.currentPageUrl];
        return page?.nextPageUrl || null;
    });

    const prevPageUrl = useSelector(state => {
        if (!state.messages.currentPageUrl) return null;
        const page = state.messages.pageMap[state.messages.currentPageUrl];
        return page?.prevPageUrl || null;
    });

    const [viewResource, setViewResource] = useState(null);
    const [showViewer, setShowViewer] = useState(false);
    const [showThanksModal, setShowThanksModal] = useState(false);
    const [replyToCommId, setReplyToCommId] = useState(null);
    const [viewMode, setViewMode] = useState("received");
    const [collapsedThreads, setCollapsedThreads] = useState({});
    const [anchorTopics, setAnchorTopics] = useState({});
    const byId = useSelector((state) => state.messages.byId);
    const providers = useSelector(state => state.providers);
    const patients = useSelector(state => state.patients);
    const [showReplyModal, setShowReplyModal] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [submissionResult, setSubmissionResult] = useState("");
    const [defaultCollapseState, setDefaultCollapseState] = useState(false); // true = collapsed by default
    const [groupingMode, setGroupingMode] = useState(localStorage.getItem("inboxGroupingMode") || "date");
    const [loadingInbox, setLoadingInbox] = useState(true);
    const commArray = byId && typeof byId === "object" ? Object.values(byId) : [];
    const [collapseManuallyToggled, setCollapseManuallyToggled] = useState(false);
    const [modalCommDetails, setModalCommDetails] = useState(null);
    const [showCommModal, setShowCommModal] = useState(false);
    const [groupByAnchor, setGroupByAnchor] = useState(() => {
        const stored = localStorage.getItem("groupByAnchor");
        return stored === null ? true : stored === "true"; // Default to grouped
    });

    const [isReplyMode, setIsReplyMode] = useState(false);


    const getDirectionLabel = (key) => {
        switch (key) {
            case "incoming": return "Inbox";
            case "outgoing": return "Sent";
            default: return "Inbox";
        }
    };

    const getFlagLabel = (key) => {
        switch (key) {
            case "all": return "All";
            case "unread": return "Unread";
            case "urgent": return "Urgent/STAT";
            case "starred": return "Starred";
            case "snoozed": return "Snoozed";
            default: return "All";
        }
    };

    const getDirectionIcon = (key) => {
        switch (key) {
            case "incoming": return <FiArrowDownRight style={{ color: "#dc3545", marginRight: "0.4rem" }} />;
            case "outgoing": return <FiArrowUpRight style={{ color: "#0d6efd", marginRight: "0.4rem" }} />;
            case "starred": return <FaStar style={{ color: "#ffc107", marginRight: "0.4rem" }} />;
            case "snoozed": return <FaRegClock style={{ color: "#6c757d", marginRight: "0.4rem" }} />;
            default: return null;
        }
    };

    const senderId = selectedComm?.sender?.reference?.split("/")[1];
    const originalRecipients = selectedComm?.recipient
        ?.map((r) => {
            const [resourceType, id] = r.reference.split("/");
            return { resourceType, id };
        })
        .filter(({ id }) => id !== senderId); // 🧼 Removes sender from recipients

    // Get all unique practitioner IDs from the Communication's recipients and sender
    const allProviderIds = [
        ...(selectedComm?.recipient?.map(r => r.reference.split("/")[1]) || []),
        selectedComm?.sender?.reference?.split("/")[1]
    ].filter(Boolean);

    // Remove duplicates and the contextProvider
    const contextProviderId = contextProvider?.id; // <-- Add this line before using it
    const uniqueProviderIds = Array.from(new Set(allProviderIds)).filter(id => id !== contextProviderId);

    // Map to provider resources, fallback to minimal Practitioner if not found
    const resolvedRecipients = uniqueProviderIds.map(id =>
        store.getState().providers?.[id] || { resourceType: "Practitioner", id }
    );



    //Direction filters---------------------------------------------------    
    const [directionFilter, setDirectionFilter] = useState("incoming"); // 'incoming', 'outgoing', or 'both'

    //Local filters
    const [flag, setFlag] = useState("all"); // starred, snoozed, unread, urgent

    const [priorityFilter, setPriorityFilter] = useState({
        routine: true,
        urgent: true,
        stat: true,
        asap: true,
        undefined: true
    });
    const [payloadQuery, setPayloadQuery] = useState("");
    //----------------------------------------------------------------

    const [showBuilderModal, setShowBuilderModal] = useState(false);
    const handleCommModalClose = () => setShowCommModal(false);
    const handleCommModalOpen = async (comm) => {
        console.log("opening modall...............");
        try {
            setModalCommDetails(comm);
            setShowCommModal(true);
        } catch (err) {
            console.error("Failed to fetch patient details", err);
            setModalCommDetails(null);
            setShowCommModal(true);
        }
    };

    const [latestTimestamp, setLatestTimestamp] = useState(null);

    //look at the first entry in the pagemap to get the total number of records. 
    const totalRecords = useSelector(state => {
        const firstPage = Object.values(state?.messages.pageMap || {})[0];
        return firstPage?.total || 0;
    });

    //We must accommodate for new messages arriving into the inbox and recalculate pagination.
    const pageSize = 10;
    const staticRecords = totalRecords; // from backend

    let liveRecords = 100;

    //const liveRecords = liveMessages.length; // new messages not yet paginated. TODO get the liveMessages from the newMessages redux store.

    const virtualTotal = staticRecords + liveRecords;
    const totalPages = Math.ceil(virtualTotal / pageSize);
    // Adjust current page if you're on page 1 and live messages exist
    const adjustedCurrentPage = currentPage === 1 ? 1 : currentPage;


    /*
    
    <span className={styles.pageInfo}>
      &nbsp;&nbsp;{`Page ${adjustedCurrentPage} of ${totalPages}`}
    </span>
    
    */

    useEffect(() => {
        if (contextPatient) {
            setFetchedPatient(contextPatient);
            console.log("✅ Synced fetchedPatient from contextPatient");
        }
    }, [contextPatient]);

    useEffect(() => {
        if (contextProvider) {
            setFetchedProvider(contextProvider);
            console.log("✅ Synced fetchedProvider from contextProvider");
        }
    }, [contextProvider]);

    useEffect(() => {
        if (contextEncounter) {
            setFetchedEncounter(contextEncounter);
            console.log("✅ Synced fetchedEncounter from contextEncounter");
        }
    }, [contextEncounter]);

    useEffect(() => {
        async function resolveAllNames() {
            const nameMap = {};

            const refsToResolve = messagesToShow
                .map(msg => msg.sender?.reference)
                .filter(ref => ref && !resolvedNames[ref]);

            for (const ref of refsToResolve) {
                const name = await resolveName(ref, store, fhirClient);
                nameMap[ref] = name || "Unknown Sender";
            }

            if (Object.keys(nameMap).length > 0) {
                setResolvedNames(prev => ({ ...prev, ...nameMap }));
            }
        }

        resolveAllNames();
    }, [messagesToShow, resolvedNames, store, fhirClient]);


    // Polls for new messages. Dispatches them to the newMessages slice    
    const pollingStartedRef = useRef(false);

    useEffect(() => {
        if (pollingStartedRef.current) return;
        if (!contextProvider?.id || !fhirClient) return;

        console.log("✅ Starting 15s polling for fetchNewMessages");
        pollingStartedRef.current = true;

        const intervalId = setInterval(() => {
            console.log("⏳ Polling fetchNewMessages...");
            dispatch(fetchNewMessages(contextProvider.id, fhirClient, viewMode));
        }, 30000);

        return () => {
            console.log("🛑 Clearing polling interval");
            clearInterval(intervalId);
        };
    }, [contextProvider?.id, fhirClient]);


    //promotes optimistic messages from pending to confirmed
    useEffect(() => {
        if (!selectedComm?.id) return;
        const anchorRef = selectedComm.partOf?.[0]?.reference || `Communication/${selectedComm.id}`;
        const intervalId = setInterval(() => {
            dispatch(promotePending(anchorRef, fhirClient)); //promotes pending messages
        }, 30000); // 🔁 every 15 seconds

        return () => clearInterval(intervalId);
    }, [selectedComm?.id, dispatch]);


    useEffect(() => {
        if (!communicationService || !contextProvider?.id || !fhirClient) {
            //console.log(communicationService);
            //console.log(contextProvider);
            //console.log(fhirClient);
            //console.log("missing dependency for initial fetch and group");
            return;
        }
        else {
            //console.log("hello, we have all dependencies for initial fetch and group");
            fetchAndGroup();
            setLoadingInbox(false);
        }

    }, [communicationService, contextProvider?.id, viewMode, fhirClient, groupByAnchor]);



    // 🔁 Keeps defaultCollapseState fresh in fetchAndGroup
    const collapseRef = useRef(defaultCollapseState);
    useEffect(() => {
        collapseRef.current = defaultCollapseState;
    }, [defaultCollapseState]);















    const groupCommunications = (comms) => {
        const threads = {};
        comms.forEach(comm => {
            const anchor = comm.partOf?.[0]?.reference || comm.id;
            if (!threads[anchor]) threads[anchor] = [];
            threads[anchor].push(comm);
        });
        return threads;
    };

    const loadNextPage = async () => {
        console.log("📥 Loading next page...", nextPageUrl);
        if (!nextPageUrl || !communicationService) return;

        await fetchAndGroup(nextPageUrl);
        dispatch(incrementPageNumber()); // ✅ use Redux
    };

    const loadPrevPage = async () => {
        console.log("Loading previous page...", prevPageUrl);
        if (!prevPageUrl || !communicationService) return;

        await fetchAndGroup(prevPageUrl);
        dispatch(decrementPageNumber()); // ✅ use Redux
    };

    async function handleRefresh() {
        console.log("Refreshing...");
        await fetchAndGroup();
        dispatch(setPageNumber(1)); // ✅ reset to first page
        dispatch(clearNewMessages()); // ✅ clear the new messages flag and icon
    }


    /** fetches, enriches, and then dispatches to redux.
     * Fetches a page of Communication resources and prepares state:
     * - Groups messages based on `groupByAnchor`
     * - Expands/collapses each thread based on `collapseRef.current`
     * - Resolves anchor topics for labeling grouped threads
     * - Updates pagination and communication state
     */
    const fetchAndGroup = async (pageUrl = null) => {
        console.log("fetching and resolving pageUrl", pageUrl);
        console.log("viewMode", viewMode);
        try {
            const fetchPage = await communicationService.getCommunicationsPage(
                contextProvider.id,
                pageSize,
                pageUrl,
                viewMode,
                dispatch,
                store.getState,
                "",
                true
            );

            const { data, nextPageUrl, prevPageUrl, total } = fetchPage;

            if (!data || data.length === 0) {
                setCollapsedThreads({});
                setAnchorTopics({});
                return;
            }

            const groupByIndividualMessage = (comms) => {
                const threads = {};
                comms.forEach((comm) => {
                    threads[comm.id] = [comm];
                });
                return threads;
            };

            const grouped = groupByAnchor ? groupCommunications(data) : groupByIndividualMessage(data);

            setCollapsedThreads(prev => {
                const updated = { ...prev };
                for (const anchor of Object.keys(grouped)) {
                    if (!(anchor in updated)) {
                        updated[anchor] = collapseRef.current;
                    }
                }
                return updated;
            });

            console.log(data);


            data.forEach(msg => {
                dispatch(addMessage({
                    ...msg,
                    meta: { isPending: false }
                }));
            });

            const anchorIds = new Set();
            data.forEach((comm) => {
                const ref = comm.partOf?.[0]?.reference;
                const id = ref?.split?.("/")[1];
                anchorIds.add(id || comm.id);
            });

            const fetchedAnchors = await Promise.all(
                Array.from(anchorIds).map((id) =>
                    fhirClient.request(`Communication/${id}`).catch((err) => {
                        console.warn(`❌ Failed to fetch anchor ${id}`, err);
                        return null;
                    })
                )
            );

            const anchorTopicMap = {};
            fetchedAnchors.forEach((anchor) => {
                if (anchor) {
                    const topic =
                        anchor.topic?.coding?.find((c) => c.display)?.display ||
                        anchor.topic?.text ||
                        "Conversation Thread";
                    anchorTopicMap[anchor.id] = topic;
                }

            });
            setAnchorTopics(anchorTopicMap);


            await Promise.all(
                Array.from(anchorIds).map(async (anchorId) => {
                    const anchorRef = `Communication/${anchorId}`;
                    try {
                        //console.log("Writing conversation into redux ", anchorRef);
                        await communicationService.writeConversationIntoRedux(anchorRef, dispatch);
                    } catch (err) {
                        console.warn(`❌ Failed to write conversation into redux for  anchor ${anchorId}`, err);
                    }
                })
            );


        } catch (error) {
            alert(error);
            console.error("💥 Error fetching communications:", error);
            setCollapsedThreads({});
            setAnchorTopics({});
        }
    };



    async function handleSelectComm(comm) {
        setFetchError(null);
        console.log(comm);
        //console.log(fetchedEncounter);

        const anchorRef = comm.partOf?.[0]?.reference; // e.g. "Communication/abc123"
        try {
            const commId = comm.id;
            setReplyToCommId(commId);

            // ✅ Fetch Patient
            const fetchedPatientData = await fetchPatientByReference(
                comm.subject?.reference,
                fhirClient,
                store.getState,
                store.dispatch
            );
            setFetchedPatient(fetchedPatientData);

            // ✅ Fetch Provider via Registry Service
            const fetchedProviderData = await fetchProviderByReference(
                comm.sender?.reference,
                fhirClient,
                store.getState,
                store.dispatch
            );
            setFetchedProvider(fetchedProviderData);

            // ✅ Fetch Encounter
            const fetchedEncounterData = await fetchEncounterByReference(
                comm.encounter?.reference,
                fhirClient,
                store.getState,
                store.dispatch
            );

            setFetchedEncounter(fetchedEncounterData);
            //console.log("context", contextEncounter);
            //console.log("fetched", fetchedEncounter);
            
            // 🧠 Check for read receipt
            const existingReceipts =
                comm.extension?.filter(
                    ext =>
                        ext.url === "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt"
                ) || [];

            //console.log("Existing Receipts", existingReceipts);

            const alreadyRead = existingReceipts.some(ext =>
                ext.extension?.some(
                    e =>
                        e.url === "user" &&
                        e.valueReference?.reference === `Practitioner/${contextProvider.id}`
                )
            );

            //console.log("Already Read", alreadyRead);
            const selfMessage = isSelfMessage(selectedComm, contextProvider.id);
            //console.log("SelfMessage", selfMessage);

            if (!alreadyRead && !selfMessage) {
                console.log(`[ReadReceipt] Marking message ${commId} as read for user ${contextProvider.id}`);

                try {
                    await communicationService.addReadReceipt(commId, contextProvider.id, dispatch);
                    console.log(`[ReadReceipt] Successfully added read receipt for ${commId} for user ${contextProvider.id}`);

                    const refreshedComm = await fhirClient.request(`Communication/${commId}`);
                    console.log(`[ReadReceipt] Fetched updated Communication resource:`, refreshedComm);

                    setSelectedComm(refreshedComm);
                    console.log(`[ReadReceipt] Updated selectedComm state.`);
                } catch (err) {
                    console.error(`[ReadReceipt] Error while processing read receipt for ${commId}:`, err);
                }
            } else {
                //console.log(`[ReadReceipt] Skipped: already read or self-sent. commId=${commId}, userId=${contextProvider.id}`);
            }


            //hydrate the conversation
            //console.log("Writing conversation into redux for anchorRef", anchorRef);
            //console.log("anchorRef", anchorRef);
            if (anchorRef && anchorRef !== "unknown") {
                await communicationService.writeConversationIntoRedux(anchorRef, dispatch);
            } else {
                console.info("Skipping writeConversationIntoRedux: invalid anchorRef", anchorRef);
            }
            setSelectedComm(comm);

        } catch (error) {
            console.log("❌ Inside the error...................................................");
            console.error("Error fetching associated data:", error);
            setFetchError("Failed to load related patient/provider/encounter data.");
        }
    }

    const selectMessageById = async (targetId) => {
        setDirectionFilter("outgoing");
        const result = await communicationService.getCommunicationsPage(
            contextProvider.id,
            pageSize,
            null,
            "sent",   //sent (system=incoming) or received (system = outgoing)   
            dispatch,
            store.getState,
            "",
            true
        );

        const fetchedMatch = result.data?.find(msg => msg.id === targetId);
        if (fetchedMatch) {
            setSelectedComm(fetchedMatch);
        } else {
            console.info("❌ Message not found after loading current view direction:", targetId);
        }
    };

    // Add this state to control which accordion items are open
    const [openAccordionKeys, setOpenAccordionKeys] = useState([]);

    // Helper to get all group keys for the current grouping mode
    const getAllGroupKeys = () => {
        return groupingMode !== "date"
            ? Object.entries(
                Object.values(byId).reduce((acc, comm) => {
                    let key = "Ungrouped";
                    if (groupingMode === "topic") {
                        key = comm.partOf?.[0]?.reference || `Communication/${comm.id}`;
                    } else if (groupingMode === "patient") {
                        key = comm.subject?.reference || "Unknown Patient";
                    } else if (groupingMode === "sender") {
                        key = comm.sender?.reference || "Unknown Sender";
                    }
                    acc[key] = acc[key] || [];
                    acc[key].push(comm);
                    return acc;
                }, {})
            ).map(([groupKey]) => groupKey)
            : [];
    };

    useEffect(() => {
        if (groupingMode === "date") return;

        const allKeys = getAllGroupKeys();

        setOpenAccordionKeys((prevKeys) => {
            if (!collapseManuallyToggled) {
                // Safe to apply defaultCollapseState on first render
                return defaultCollapseState ? [] : allKeys;
            }

            // ⚠️ Respect user override
            return prevKeys.filter((key) => allKeys.includes(key));
        });
    }, [groupingMode, byId]);


    return (
        <Container fluid className="mt-1">
            <Row>
                {/* East Section (2/3 width) */}
                <Col md={5} className={styles.eastSection}>
                    <div>
                        {/* Pagination Controls */}
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            {/* Left Side: NEW Button */}
                            <div>
                                <OverlayTrigger placement="bottom" overlay={tooltipRegistry.composeButton}>
                                    <Button className="mx-0"
                                        variant="outline-primary"
                                        onClick={() => {
                                            setReplyToCommId(null); // ✅ ensure compose flow uses contextPatient
                                            setSelectedComm(null); // 🔥 Clear selected thread
                                            setIsReplyMode(false); // 🔹 Explicitly mark as reply
                                            setShowReplyModal(true);

                                        }}
                                    >
                                        <FaPen className="mx-0" /> New
                                    </Button>
                                </OverlayTrigger>
                                <OverlayTrigger placement="bottom" overlay={tooltipRegistry.replyButton}>
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => {
                                            setIsReplyMode(true); // 🔹 Explicitly mark as reply
                                            setShowReplyModal(true);
                                        }}
                                        className="mx-0"
                                        disabled={
                                            !fetchedProvider ||
                                            !selectedComm ||
                                            (fetchedEncounter &&
                                                typeof fetchedEncounter === "object" &&
                                                "error" in fetchedEncounter &&
                                                typeof fetchedEncounter.error === "number")
                                        }
                                    >
                                        Reply
                                    </Button>
                                </OverlayTrigger>

                                <Button variant="outline-primary" onClick={() => setShowBuilderModal(true)}>
                                    <FiSearch />
                                    Search
                                </Button>

                                <CommQueryBuilderModal
                                    show={showBuilderModal}
                                    onClose={() => setShowBuilderModal(false)}
                                    fhirClient={fhirClient}
                                    contextProvider={contextProvider}
                                    setSelectedComm={setSelectedComm}

                                />
                                <Button
                                    variant="outline-primary"
                                    onClick={handleRefresh}
                                    className={`mx-0 position-relative ${hasNewMessage ? styles.newMessageHighlight : ""}`}
                                >
                                    Refresh
                                    {hasNewMessage && (
                                        <span
                                            style={{
                                                position: "absolute",
                                                top: "-6px",
                                                right: "-6px",
                                                width: "18px",
                                                height: "18px",
                                                background: "red",
                                                borderRadius: "50%",
                                                border: "2px solid white",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "white",
                                                fontWeight: "bold",
                                                fontSize: "1rem",
                                                zIndex: 10,
                                                boxShadow: "0 0 6px 2px #ff000080"
                                            }}
                                            title="New message(s) have arrived!"
                                        >
                                            !
                                        </span>
                                    )}
                                </Button>


                            </div>

                            {/* Right Side: Pagination + Refresh */}
                            <div className="d-flex justify-content-between align-items-center text-end">
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={loadPrevPage}
                                    disabled={!prevPageUrl}
                                    className="mx-1 d-flex align-items-center"
                                    title="Previous Page"
                                >
                                    <span style={{ fontSize: "1.5rem", lineHeight: "1" }}>⏪</span>
                                </Button>

                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={loadNextPage}
                                    disabled={!nextPageUrl}
                                    className="mx-1 d-flex align-items-center"
                                    title="Next Page"
                                >
                                    <span style={{ fontSize: "1.5rem", lineHeight: "1" }}>⏩</span>
                                </Button>


                                <div className="d-flex align-items-center ms-3">
                                    <span className="ms-0 text-dark">Page&nbsp;</span>
                                    <input
                                        type="number"
                                        min={1}
                                        max={Math.ceil(totalRecords / 10)}
                                        value={currentPage}
                                        disabled
                                        onChange={(e) => {
                                            const page = Math.max(1, Math.min(Math.ceil(totalRecords / 10), Number(e.target.value)));
                                            dispatch(setPageNumber(page));

                                            // TODO: Map page number to page URL from pageMap
                                            // const targetUrl = Object.keys(pageMap).find(key => pageMap[key]?.pageNumber === page);
                                            // if (targetUrl) {
                                            //   dispatch(setCurrentPageUrl(targetUrl));
                                            //   fetchAndGroup(targetUrl);
                                            // }
                                        }}
                                        className="form-control form-control-sm text-center"
                                        style={{ width: "50px" }}
                                    />
                                    <span className="ms-1 text-dark">of {Math.max(1, Math.ceil(totalRecords / 10))}</span>
                                </div>
                            </div>
                        </div>


                        {error ? (
                            <Alert variant="danger">{error}</Alert>
                        ) : messagesToShow.length > 0 ? (
                            <>
                                <div className="d-flex align-items-center justify-content-start gap-3" style={{ marginTop: "0.3rem" }}>
                                    {/* Grouping Select */}
                                    <div className="d-flex align-items-center">
                                        <Form.Label htmlFor="grouping-mode" className="mb-0 me-2 text-dark"></Form.Label>
                                        <Form.Select
                                            id="grouping-mode"
                                            value={groupingMode}
                                            onChange={(e) => {
                                                const mode = e.target.value;
                                                setGroupingMode(mode);
                                                localStorage.setItem("inboxGroupingMode", mode);
                                            }}
                                            className="mb-1"
                                            style={{ width: "120px" }}
                                        >
                                            <option value="date">By Date</option>
                                            <option value="topic">By Topic</option>
                                            <option value="patient">By Patient</option>
                                            <option value="sender">By Sender</option>
                                        </Form.Select>
                                    </div>

                                    {/* Direction Filter Select */}
                                    <div className="d-flex align-items-center">
                                        <Dropdown onSelect={async (key) => {
                                            //console.log("Selected direction filter:", key);
                                            //console.log("Current direction filter:", directionFilter);
                                            setDirectionFilter(key);
                                            setSelectedComm(null); // 🔄 Clear the thread viewer


                                            if (key === "incoming" || key === "outgoing") {
                                                const direction = key === "incoming" ? "received" : "sent";
                                                //console.log(direction);
                                                try {
                                                    await communicationService.getCommunicationsPage(
                                                        contextProvider.id,
                                                        pageSize,
                                                        null, // no pageUrl for initial load
                                                        direction,
                                                        dispatch,
                                                        store.getState,
                                                        "", //extra params
                                                        true
                                                    );
                                                } catch (err) {
                                                    console.error("Failed to fetch communications:", err);
                                                }

                                                //console.log(key);

                                            } //else {
                                            // For starred or snoozed, filter locally
                                            // console.log(key);
                                            //setDirectionFilter(key);
                                            //}
                                        }}>
                                            <Dropdown.Toggle
                                                variant="outline-secondary"
                                                id="direction-filter"
                                                className={styles.dropdownToggle}

                                            >
                                                {getDirectionIcon(directionFilter)}
                                                {getDirectionLabel(directionFilter)}
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu className={styles.dropdownMenu}>

                                                <Dropdown.Item eventKey="incoming">
                                                    <FiArrowDownRight style={{ color: "#dc3545", marginRight: "0.5rem" }} />
                                                    Inbox
                                                </Dropdown.Item>
                                                <Dropdown.Item eventKey="outgoing">
                                                    <FiArrowUpRight style={{ color: "#0d6efd", marginRight: "0.5rem" }} />
                                                    Sent
                                                </Dropdown.Item>

                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>

                                    {/* Local Message filtering */}
                                    <div className="d-flex align-items-center">
                                        <Dropdown onSelect={async (key) => {
                                            console.log(key);
                                            getFlagLabel(key);
                                            setFlag(key);
                                        }}>
                                            <Dropdown.Toggle
                                                variant="outline-secondary"
                                                id="direction-filter"
                                                className={styles.dropdownToggle}

                                            >
                                                {getDirectionIcon(directionFilter)}
                                                {getFlagLabel(flag)}
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu className={styles.dropdownMenu}>

                                                <Dropdown.Item eventKey="all">
                                                    <FaRegCommentDots style={{ color: "#000000ff", marginRight: "0.5rem" }} />
                                                    All
                                                </Dropdown.Item>

                                                <Dropdown.Item eventKey="starred">
                                                    <FaStar style={{ color: "#ffc107", marginRight: "0.5rem" }} />
                                                    Starred
                                                </Dropdown.Item>
                                                <Dropdown.Item eventKey="snoozed">
                                                    <FiClock style={{ color: "#0077ffff", marginRight: "0.5rem" }} />
                                                    Snoozed
                                                </Dropdown.Item>

                                                <Dropdown.Item eventKey="unread">
                                                    <FaEye style={{ color: "#ff8800ff", marginRight: "0.5rem" }} />
                                                    Unread
                                                </Dropdown.Item>


                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>



                                    {groupingMode !== "date" && (
                                        <Form.Check
                                            type="switch"
                                            id="expand-collapse-all"
                                            label={
                                                <span style={{ color: "black" }}>
                                                    {defaultCollapseState ? "Expand All" : "Collapse All"}
                                                </span>
                                            }
                                            checked={!defaultCollapseState}
                                            onChange={() => {
                                                const newState = !defaultCollapseState;
                                                const allKeys = getAllGroupKeys();

                                                setDefaultCollapseState(newState);
                                                setCollapseManuallyToggled(true); // 🔒 user override is now active
                                                setOpenAccordionKeys(newState ? [] : allKeys);

                                                setCollapsedThreads((oldThreads) => {
                                                    const updated = { ...oldThreads };
                                                    allKeys.forEach((key) => {
                                                        updated[key] = newState;
                                                    });
                                                    return updated;
                                                });
                                            }}
                                            style={{ marginLeft: "1rem", marginTop: "0.4rem", display: "inline-block" }}
                                        />
                                    )}
                                </div>

                                {/* Bootstrap Accordion for groupings */}
                                {groupingMode !== "date" ? (
                                    <Accordion
                                        activeKey={openAccordionKeys}
                                        onSelect={(eventKey) => {
                                            setOpenAccordionKeys((prev) =>
                                                prev.includes(eventKey)
                                                    ? prev.filter((k) => k !== eventKey)
                                                    : [...prev, eventKey]
                                            );
                                        }}
                                    >
                                        {Object.entries(
                                            Object.values(messagesToShow).reduce((acc, comm) => {
                                                //console.log(messagesToShow);
                                                let key = "Ungrouped";
                                                if (groupingMode === "topic") {
                                                    key = comm.partOf?.[0]?.reference || `Communication/${comm.id}`;
                                                } else if (groupingMode === "patient") {
                                                    key = comm.subject?.reference || "Unknown Patient";
                                                } else if (groupingMode === "sender") {
                                                    key = comm.sender?.reference || "Unknown Sender";
                                                }
                                                acc[key] = acc[key] || [];
                                                acc[key].push(comm);
                                                return acc;
                                            }, {})
                                        )
                                            .sort(([, msgsA], [, msgsB]) => {
                                                const firstSentA = new Date(msgsA[0]?.sent || 0);
                                                const firstSentB = new Date(msgsB[0]?.sent || 0);
                                                return firstSentB - firstSentA;
                                            })
                                            .map(([groupKey, messages], idx) => {
                                                let label = "Conversations";
                                                if (groupingMode === "topic") {
                                                    const anchorId = groupKey?.split("/")?.[1];
                                                    label = anchorTopics[anchorId] || "Conversation Thread";
                                                } else if (groupingMode === "patient") {
                                                    const subjectRef = messages[0]?.subject?.reference;
                                                    const patientId = subjectRef?.split("/")?.[1];
                                                    let patient = patientId ? patients[patientId] : null;
                                                    if (subjectRef && !patient) {
                                                        fetchPatientByReference(subjectRef, fhirClient, store.getState, dispatch);
                                                    }
                                                    label = patient?.name?.[0]?.text || [patient?.name?.[0]?.given?.join(" "), patient?.name?.[0]?.family].filter(Boolean).join(" ") || messages[0]?.sender?.display || "Unknown Sender";
                                                } else if (groupingMode === "sender") {
                                                    const senderRef = messages[0]?.sender?.reference;
                                                    const senderId = senderRef?.split("/")?.[1];
                                                    let provider = senderId ? providers[senderId] : null;
                                                    if (senderRef && !provider) {
                                                        fetchProviderByReference(senderRef, fhirClient, store.getState, dispatch);
                                                    }
                                                    label = provider?.name?.[0]?.text || [provider?.name?.[0]?.given?.join(" "), provider?.name?.[0]?.family].filter(Boolean).join(" ") || messages[0]?.sender?.display || "Unknown Sender";
                                                }

                                                return (
                                                    <Accordion.Item eventKey={groupKey} key={groupKey}>
                                                        <Accordion.Header>
                                                            {label}
                                                            <span className="ms-2 text-muted">
                                                                ({messages.length} total • {
                                                                    messages.filter(comm => !hasBeenReadByCurrentUser(comm, contextProvider?.id)).length
                                                                } unread
                                                                {getUrgentCount(messages) > 0 && (
                                                                    <> • <span className="text-danger fw-bold">🔥 {getUrgentCount(messages)} urgent</span></>
                                                                )}
                                                                {getStarredCount(messages, contextProvider?.id) > 0 && (
                                                                    <> • <span className="text-warning fw-bold"><FaStar style={{ marginRight: "0.3rem" }} />{getStarredCount(messages, contextProvider?.id)} starred</span></>
                                                                )})
                                                            </span>
                                                        </Accordion.Header>
                                                        <Accordion.Body>
                                                            <ListGroup>
                                                                {filterLocalMessages(messages, {
                                                                    flag: flag,
                                                                    priorityFilter,
                                                                    practitionerId: contextProvider?.id
                                                                })
                                                                    .sort((a, b) => new Date(b.sent) - new Date(a.sent))
                                                                    .map((comm, index) => {

                                                                        //console.log(comm);
                                                                        const senderRef = comm.sender?.reference;
                                                                        const senderId = senderRef?.split("/")?.[1];
                                                                        const sender = senderId ? providers[senderId] : null;
                                                                        const hasAttachment = comm.payload?.some(
                                                                            (p) => p.contentAttachment
                                                                        );

                                                                        // Practitioner-aware status check
                                                                        const hasStatusFlag = (comm, type, practitionerId) =>
                                                                            comm.extension?.some(ext => {
                                                                                if (ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return false;
                                                                                const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                                                                                const flagCode = ext.extension?.find(e => e.url === "flag")?.valueCode;
                                                                                const normalizedRef = userRef?.split("/")?.pop();
                                                                                return normalizedRef === practitionerId && flagCode === type;
                                                                            });

                                                                        const isStarred = hasStatusFlag(comm, "starred", contextProvider?.id);
                                                                        const isSnoozed = hasStatusFlag(comm, "snoozed", contextProvider?.id);

                                                                        if (senderRef && !sender) {
                                                                            fetchProviderByReference(
                                                                                senderRef,
                                                                                fhirClient,
                                                                                store.getState,
                                                                                dispatch
                                                                            );
                                                                        }

                                                                        /*
                                                                        const senderName =
                                                                            sender?.name?.[0]?.text ||
                                                                            [sender?.name?.[0]?.given?.join(" "), sender?.name?.[0]?.family]
                                                                                .filter(Boolean)
                                                                                .join(" ") ||
                                                                            "Unknown Sender";
                                                                        */
                                                                        const senderName = resolvedNames[comm.sender?.reference] || "Unknown Sender";

                                                                        const isUnread = !hasBeenReadByCurrentUser(comm, contextProvider?.id);
                                                                        const isOutgoing = senderRef === `Practitioner/${contextProvider?.id}`;
                                                                        const directionIcon = isOutgoing ? (
                                                                            <FiArrowUpRight
                                                                                style={{ color: "#0d6efd", marginRight: "0.0rem" }}
                                                                                title="Outgoing"
                                                                            />
                                                                        ) : (
                                                                            <FiArrowDownRight
                                                                                style={{ color: "#dc3545", marginRight: "0.0rem" }} // 🔴 red for incoming
                                                                                title="Incoming"
                                                                            />
                                                                        );
                                                                        return (
                                                                            <ListGroup.Item
                                                                                key={comm.id || index}
                                                                                action
                                                                                active={selectedComm?.id === comm.id}
                                                                                onClick={() => {
                                                                                    setSelectedComm(comm); // updates selection immediately
                                                                                    setTimeout(() => { handleSelectComm(comm); }, 0);
                                                                                }}

                                                                                onDoubleClick={() => handleCommModalOpen(comm)}
                                                                                className={
                                                                                    selectedComm?.id === comm.id
                                                                                        ? styles.selectedMessageItem
                                                                                        : styles.messageItem
                                                                                }
                                                                                style={{ marginLeft: "0.0rem" }}
                                                                            >
                                                                                <div className="d-flex justify-content-between align-items-center">
                                                                                    <div>
                                                                                        <div className="d-flex align-items-center">
                                                                                            {renderPriorityBadge(comm.priority)}
                                                                                            <strong
                                                                                                style={{
                                                                                                    marginLeft: "0.5rem",
                                                                                                    color: isUnread ? "#0d6efd" : "#444",
                                                                                                    fontWeight: isUnread ? "bold" : "normal"
                                                                                                }}
                                                                                            >
                                                                                                {comm.topic?.coding?.[0]?.display || "(No topic)"}
                                                                                            </strong>
                                                                                            <span style={{ marginLeft: "0.3rem" }}>{directionIcon}</span>
                                                                                            {hasAttachment && (
                                                                                                <FaPaperclip
                                                                                                    title="Has attachment"
                                                                                                    style={{
                                                                                                        marginLeft: "0.5rem",
                                                                                                        fontSize: "0.9rem",
                                                                                                        color: "#6c757d"
                                                                                                    }}
                                                                                                />
                                                                                            )}
                                                                                        </div>
                                                                                        <div style={{ fontSize: "0.8rem", color: "#444" }}>
                                                                                            From: {senderName} —{" "}
                                                                                            {comm.payload?.[0]?.contentString?.slice(0, 100) ||
                                                                                                "(No content)"}
                                                                                        </div>
                                                                                        <small>Sent: {formatDate(comm.sent)}</small>
                                                                                    </div>

                                                                                    {/* ⭐ ⏰ Status controls */}
                                                                                    <div
                                                                                        className="d-flex align-items-center"
                                                                                        style={{ minWidth: "80px", justifyContent: "flex-end", gap: "0.75rem" }}
                                                                                    >
                                                                                        <FaStar
                                                                                            title={isStarred ? "Unstar this message" : "Star this message"}
                                                                                            className={`${styles.starIcon} ${isStarred ? styles.starIconActive : ""}`}
                                                                                            onClick={() => {
                                                                                                if (isStarred) {
                                                                                                    communicationService.removeStatusFlag(comm.id, "starred", contextProvider.id, dispatch);
                                                                                                } else {
                                                                                                    communicationService.addStatusFlag(comm.id, "starred", contextProvider.id, dispatch);
                                                                                                }
                                                                                            }}
                                                                                        />

                                                                                        <FiClock
                                                                                            title={isSnoozed ? "Unsnooze this message" : "Snooze this message"}
                                                                                            className={`${styles.snoozeIcon} ${isSnoozed ? styles.snoozeIconActive : ""}`}
                                                                                            onClick={() => {
                                                                                                if (isSnoozed) {
                                                                                                    communicationService.removeStatusFlag(comm.id, "snoozed", contextProvider.id, dispatch);
                                                                                                } else {
                                                                                                    communicationService.addStatusFlag(comm.id, "snoozed", contextProvider.id, dispatch);
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </ListGroup.Item>
                                                                        );
                                                                    })}
                                                            </ListGroup>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                );
                                            })}
                                    </Accordion>
                                ) : (
                                    <Accordion alwaysOpen defaultActiveKey={["Today", "Yesterday", "Last Week", "Older"]}>
                                        {Object.entries(groupByDateRange(messagesToShow)).map(([sectionLabel, messages]) => messages.length > 0 ? (
                                            <Accordion.Item eventKey={sectionLabel} key={sectionLabel}>
                                                <Accordion.Header>
                                                    {sectionLabel}
                                                    <span className="ms-2 text-muted">
                                                        ({messages.length} total • {
                                                            messages.filter(comm => !hasBeenReadByCurrentUser(comm, contextProvider?.id)).length
                                                        } unread
                                                        {getUrgentCount(messages) > 0 && (
                                                            <> • <span className="text-danger fw-bold">🔥 {getUrgentCount(messages)} urgent</span></>
                                                        )}
                                                        {getStarredCount(messages, contextProvider?.id) > 0 && (
                                                            <> • <span className="text-warning fw-bold"><FaStar style={{ marginRight: "0.3rem" }} />{getStarredCount(messages, contextProvider?.id)} starred</span></>
                                                        )})
                                                    </span>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <ListGroup>
                                                        {filterLocalMessages(messages, {
                                                            priorityFilter,
                                                            practitionerId: contextProvider?.id,
                                                            flag: flag, // or "snoozed", etc. depending on your current view
                                                            payloadQuery: payloadQuery
                                                        }).map((comm, index) => {

                                                            //console.log(messages);

                                                            const senderRef = comm.sender?.reference;
                                                            const senderId = senderRef?.split("/")?.[1];
                                                            const sender = senderId ? providers[senderId] : null;

                                                            if (senderRef && !sender) {
                                                                fetchProviderByReference(senderRef, fhirClient, store.getState, dispatch);
                                                            }

                                                            //const senderName = sender?.name?.[0]?.text || [sender?.name?.[0]?.given?.join(" "), sender?.name?.[0]?.family].filter(Boolean).join(" ") || "Unknown Sender";

                                                            const senderName = resolvedNames[comm.sender?.reference] || "Unknown Sender";
                                                            const isUnread = !hasBeenReadByCurrentUser(comm, contextProvider?.id);
                                                            const isOutgoing = senderRef === `Practitioner/${contextProvider?.id}`;
                                                            const directionIcon = isOutgoing ? (
                                                                <FiArrowUpRight style={{ color: "#0d6efd" }} title="Outgoing" />
                                                            ) : (
                                                                <FiArrowDownRight style={{ color: "#dc3545" }} title="Incoming" />
                                                            );

                                                            const hasAttachment = comm.payload?.some(p => p.contentAttachment);

                                                            // Practitioner-aware status check
                                                            const hasStatusFlag = (comm, type, practitionerId) =>
                                                                comm.extension?.some(ext => {
                                                                    if (ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return false;
                                                                    const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                                                                    const flagCode = ext.extension?.find(e => e.url === "flag")?.valueCode;
                                                                    const normalizedRef = userRef?.split("/")?.pop();
                                                                    return normalizedRef === practitionerId && flagCode === type;
                                                                });

                                                            const isStarred = hasStatusFlag(comm, "starred", contextProvider?.id);
                                                            const isSnoozed = hasStatusFlag(comm, "snoozed", contextProvider?.id);

                                                            return (
                                                                <ListGroup.Item
                                                                    key={comm.id || index}
                                                                    action
                                                                    active={selectedComm?.id === comm.id}
                                                                    onClick={() => {
                                                                        setSelectedComm(comm); // updates selection immediately
                                                                        setTimeout(() => {
                                                                            handleSelectComm(comm);
                                                                        }, 0);
                                                                    }}
                                                                    onDoubleClick={() => handleCommModalOpen(comm)}
                                                                    className={selectedComm?.id === comm.id ? styles.selectedMessageItem : styles.messageItem}
                                                                    style={{ marginLeft: "0.0rem" }}
                                                                >
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <div>
                                                                            <div className="d-flex align-items-center">
                                                                                {renderPriorityBadge(comm.priority)}
                                                                                <strong style={{
                                                                                    marginLeft: "0.5rem",
                                                                                    color: isUnread ? "#0d6efd" : "#444",
                                                                                    fontWeight: isUnread ? "bold" : "normal"
                                                                                }}>
                                                                                    {comm.topic?.coding?.[0]?.display || "(No topic)"}
                                                                                </strong>
                                                                                <span style={{ marginLeft: "0.3rem" }}>{directionIcon}</span>
                                                                                {hasAttachment && (
                                                                                    <FaPaperclip
                                                                                        title="Has attachment"
                                                                                        style={{ marginLeft: "0.5rem", fontSize: "0.9rem", color: "#6c757d" }}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                            <div style={{ fontSize: "0.8rem", color: "#444" }}>
                                                                                From: {senderName} — {comm.payload?.[0]?.contentString?.slice(0, 100) || "(No content)"}
                                                                            </div>
                                                                            <small>Sent: {formatDate(comm.sent)}</small>
                                                                        </div>

                                                                        {/* ⭐ ⏰ Status controls */}
                                                                        <span className="d-flex align-items-center gap-2">
                                                                            <FaStar
                                                                                title={isStarred ? "Unstar this message" : "Star this message"}
                                                                                className={`${styles.starIcon} ${isStarred ? styles.starIconActive : ""}`}
                                                                                onClick={() => {
                                                                                    console.log("clicked");
                                                                                    if (isStarred) {
                                                                                        communicationService.removeStatusFlag(comm.id, "starred", contextProvider.id, dispatch);
                                                                                    } else {
                                                                                        communicationService.addStatusFlag(comm.id, "starred", contextProvider.id, dispatch);
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <FiClock
                                                                                title={isSnoozed ? "Unsnooze this message" : "Snooze this message"}
                                                                                style={{
                                                                                    cursor: "pointer",
                                                                                    color: isSnoozed ? "#f00de8ff" : "#6c757d",
                                                                                    fontSize: "1.2rem"
                                                                                }}
                                                                                onClick={() => {
                                                                                    if (isSnoozed) {
                                                                                        communicationService.removeStatusFlag(comm.id, "snoozed", contextProvider.id, dispatch);
                                                                                    } else {
                                                                                        communicationService.addStatusFlag(comm.id, "snoozed", contextProvider.id, dispatch);
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </span>
                                                                    </div>
                                                                </ListGroup.Item>
                                                            );
                                                        })}
                                                    </ListGroup>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        ) : null
                                        )}
                                    </Accordion>
                                )}

                                {fetchError && <Alert variant="danger">{fetchError}</Alert>}
                                {/* Reply Buttons */}
                                <Row className="mt-2">
                                    <Col md={12} className="text-start">
                                        {/* DISABLED FOR NOW......<Modal show={showThanksModal} onHide={() => setShowThanksModal(false)} centered> disabling for now*/}
                                        <QuickThanksModal show={showThanksModal} onClose={() => setShowThanksModal(false)} />
                                    </Col>
                                </Row>
                            </>
                        ) : (


                            <div>
                                {loadingInbox ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "150px" }}>
                                        <Spinner animation="border" role="status" variant="primary">
                                            <span className="visually-hidden">Loading inbox...</span>
                                        </Spinner>
                                        <p className="mt-2" style={{ color: "gray" }}>Loading inbox…</p>
                                    </div>
                                ) : commArray.length === 0 ? (
                                    <p style={{ color: "black" }}>Communications</p>
                                ) : (
                                    <div></div>
                                )}
                            </div>
                        )}
                    </div>

                </Col>
                <Col md={7}>
                    <InfoBar
                        fhirClient={fhirClient}
                        contextPatient={fetchedPatient}
                        providerData={fetchedProvider}
                        encounterData={fetchedEncounter}
                        layout="horizontal" // Optional, can be "vertical" or "horizontal"
                    />


                    <ThreadedMessageViewer
                        selectedComm={selectedComm}
                        fhirClient={fhirClient}
                        contextProvider={contextProvider}
                        communicationService={communicationService}
                        userSelection={true}
                        fetchedPatient={fetchedPatient}
                        fetchedProvider={fetchedProvider}
                        //selectMessageById={selectMessageById}
                        fetchedEncounter={fetchedEncounter}
                        setShowThanksModal={setShowThanksModal}
                        setShowReplyModal={setShowReplyModal}
                        setSelectedComm={setSelectedComm}
                        onSendLocalReply={(msg) => {
                            setSelectedComm((prev) => ({
                                ...prev,
                                injectedMessages: [...(prev?.injectedMessages || []), msg]
                            }));
                        }}
                    />

                </Col>
            </Row>

            <CommViewerModal
                show={showCommModal}
                onClose={handleCommModalClose}
                resource={modalCommDetails}
                title="Communication Details"
                fhirClient={fhirClient}
                onReply={(comm) => {
                    //setReplyTarget(comm);
                    //setReplyMode("reply");
                    setShowReplyModal(true);
                }}
                onReplyAll={(comm) => {
                    //setReplyTarget(comm);
                    //setReplyMode("replyAll");
                    setShowReplyModal(true);
                }}
            />


            <CommunicationFormModal
                show={showReplyModal}
                onClose={() => setShowReplyModal(false)}
                formSubmitted={formSubmitted}
                submissionResult={submissionResult}
                setFormSubmitted={setFormSubmitted}
                setSubmissionResult={setSubmissionResult}
                selectMessageById={selectMessageById}
                setSelectedComm={setSelectedComm}
                contextProvider={contextProvider}
                contextEncounter={contextEncounter}
                replyToCommId={replyToCommId}
                fetchedPatient={isReplyMode ? fetchedPatient : contextPatient}
                fetchedEncounter={isReplyMode ? fetchedEncounter : contextEncounter}
                encounter={fetchedEncounter}
                communicationService={communicationService}
                resolvedRecipients={resolvedRecipients}
                fhirClient={fhirClient}
                selectedComm={selectedComm}
                isReplyMode={isReplyMode} // ✅ New flag

            />

            {/* FHIR Resource Viewer Modal */}
            {showViewer && (
                <GenericFHIRViewerModal
                    show={showViewer}
                    resource={viewResource}
                    onClose={() => setShowViewer(false)}
                />
            )}
        </Container>

    );
}

export default Inbox;